import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import type {
  StAdjustmentPushIntent,
  StAdjustmentTechnicianGroup,
} from "./types";
import { ST_ADJUSTMENTS_ACCESS, type StAdjustmentsAccess } from "./types";
import { useStAdjustmentPush } from "./use-st-adjustment-push";
import { useStAdjustmentsData } from "./use-st-adjustments-data";
import { useTechnicianSelection } from "./use-technician-selection";

const EMPTY_GROUPS: StAdjustmentTechnicianGroup[] = [];

interface StAdjustmentsSectionInput {
  businessId: string | null;
  runId: string | null;
  access: StAdjustmentsAccess;
  // False when another payroll section is visible. The push watcher stays bound
  // to the run, while view-only data can idle until PushPay is visible again.
  enabled: boolean;
  // Roster snapshot the run was calculated against, for technician names.
  rosterVersionNumber: number | null;
}

const useStAdjustmentsSectionState = ({
  businessId,
  runId,
  access,
  enabled,
  rosterVersionNumber,
}: StAdjustmentsSectionInput) => {
  const {
    data: summary,
    isLoading,
    error: summaryError,
    isPreview,
  } = useStAdjustmentsData({
    businessId,
    runId,
    access,
    rosterVersionNumber,
    enabled,
  });
  // One push per run, owned here — this is the only place that starts or watches
  // one. Preview and hidden access keep the run only as UI state identity; they
  // never bind the live watcher or mutation to it.
  const liveRunId = access === ST_ADJUSTMENTS_ACCESS.enabled ? runId : null;
  const push = useStAdjustmentPush(businessId, liveRunId);

  const [detailTechnicianId, setDetailTechnicianId] = useState<number | null>(
    null,
  );
  // A push staged for confirmation. Non-null is what opens the dialog, so the
  // figures it shows are the exact ones the request will carry.
  const [pendingIntent, setPendingIntent] =
    useState<StAdjustmentPushIntent | null>(null);

  const byTechnician = summary?.by_technician ?? EMPTY_GROUPS;

  const selection = useTechnicianSelection({ groups: byTechnician });

  // Run-change resets. These used to come free from a keyed boundary around the
  // view; the state now lives in a provider mounted above the whole page, which
  // a period switch must not remount (it would take the Grid with it). Adjusting
  // during render rather than in an effect keeps a stale selection or an open
  // panel from being painted against the new run for a frame.
  const runKey = `${businessId ?? ""}:${runId ?? ""}:${access}`;
  const [seenRunKey, setSeenRunKey] = useState(runKey);
  if (seenRunKey !== runKey) {
    setSeenRunKey(runKey);
    setDetailTechnicianId(null);
    setPendingIntent(null);
    selection.clear();
  }

  // The open panel is derived from an id, not a stored row, so the sidebar
  // re-renders with fresh tallies as a push invalidates the summary.
  const detailGroup =
    byTechnician.find((group) => group.employee_id === detailTechnicianId) ??
    null;
  const closeDetail = useCallback(() => setDetailTechnicianId(null), []);

  const totals = summary?.totals;
  const warnings = summary?.warnings ?? [];
  const pending = totals?.pending ?? 0;
  const hasRows = !isLoading && !summaryError && !!totals && totals.count > 0;

  // The action pushes the checked technicians, or everything pending when none
  // are checked. Both are server-side, so neither enumerates line keys.
  const pushExpected = selection.hasSelection
    ? selection.selectedLines
    : pending;
  const pushCents = selection.hasSelection
    ? selection.selectedCents
    : (totals?.pending_amount_cents ?? 0);

  // Nothing is sent straight from a click: every route stages an intent, and the
  // confirmation dialog is what turns it into a request.
  const stagePush = useCallback(
    () =>
      setPendingIntent({
        technicianIds: selection.hasSelection
          ? selection.selectedGroups.map((g) => g.employee_id)
          : null,
        lineCount: pushExpected,
        amountCents: pushCents,
      }),
    [selection.hasSelection, selection.selectedGroups, pushExpected, pushCents],
  );

  const stagePushTechnician = useCallback(
    (group: StAdjustmentTechnicianGroup) => {
      if (!group.employee_id) return;

      setPendingIntent({
        technicianIds: [group.employee_id],
        lineCount: group.pending,
        amountCents: group.pending_amount_cents,
        technicianName: group.technicianName,
      });
    },
    [],
  );

  // The dialog stays open, and locked, until the enqueue settles. Clearing the
  // intent on the click instead closed it before `isEnqueuing` could ever render,
  // so the pending state the dialog is built around never appeared and a slow
  // request looked like nothing had happened.
  const confirmPush = useCallback(() => {
    if (!pendingIntent) return;

    push.start({
      ...(pendingIntent.technicianIds && {
        technicianIds: pendingIntent.technicianIds,
      }),
      expected: pendingIntent.lineCount,
      onSettled: () => setPendingIntent(null),
    });
  }, [pendingIntent, push]);

  return {
    businessId,
    runId,
    isPreview,
    isLoading,
    summaryError,
    byTechnician,
    totals,
    warnings,
    hasRows,
    push,
    selection,
    detailTechnicianId,
    setDetailTechnicianId,
    detailGroup,
    closeDetail,
    pendingIntent,
    setPendingIntent,
    pushExpected,
    pushCents,
    stagePush,
    stagePushTechnician,
    confirmPush,
  };
};

type StAdjustmentsSectionValue = ReturnType<
  typeof useStAdjustmentsSectionState
>;

const StAdjustmentsSectionContext =
  createContext<StAdjustmentsSectionValue | null>(null);

/**
 * Puts the PushPay controller above the page header.
 *
 * The section's push action belongs beside the period picker and its
 * technician detail belongs in the page's shared sidebar, but both render as
 * siblings far from the rollup table in the same page. That used to be bridged
 * by portaling into DOM nodes the shell exposed, which put the React tree out of
 * step with the DOM — the same trade `PacksSectionProvider` documents. Holding
 * the state above all three readers instead makes it ordinary React.
 *
 * Keep the current `runId` attached while another tab is visible so an active
 * push can finish its status watch and report the result. Set `enabled` false to
 * idle the summary and roster queries. The provider must stay mounted: swapping
 * it per active section would remount the Grid, whose workbook and selection are
 * kept alive across tab switches.
 */
export const StAdjustmentsSectionProvider = ({
  children,
  ...input
}: StAdjustmentsSectionInput & { children: ReactNode }) => {
  const value = useStAdjustmentsSectionState(input);

  return (
    <StAdjustmentsSectionContext.Provider value={value}>
      {children}
    </StAdjustmentsSectionContext.Provider>
  );
};

export const useStAdjustmentsSection = () => {
  const section = useContext(StAdjustmentsSectionContext);
  if (!section) {
    throw new Error(
      "PushPay components must be rendered inside a StAdjustmentsSectionProvider.",
    );
  }
  return section;
};
